/**
 * @module features/users/api/users-api
 * @description TanStack Query hooks and API functions for Super Admin User Management.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope } from "@/lib/api-client";
import { UserDto } from "@/types/auth";
import {
  PaginationMeta,
  UserStats,
  CreateUserInput,
  UpdateUserInput,
  PaginatedUsersResponse,
} from "@/types/users";

export interface UsersQueryParams {
  page?: number;
  page_size?: number;
  role?: string;
  search?: string;
}

export async function getUsers(
  params: UsersQueryParams = {}
): Promise<PaginatedUsersResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set("page", String(params.page));
  if (params.page_size) queryParams.set("page_size", String(params.page_size));
  if (params.role && params.role !== "all") queryParams.set("role", params.role);
  if (params.search) queryParams.set("search", params.search);

  const url = `/super-admin/users/?${queryParams.toString()}`;
  const response = await apiClient.get<
    ApiSuccessEnvelope<UserDto[]> & { pagination: PaginationMeta }
  >(url);

  return {
    items: response.data.data,
    pagination: response.data.pagination,
  };
}

export async function getUserStats(): Promise<UserStats> {
  const response = await apiClient.get<ApiSuccessEnvelope<UserStats>>(
    "/super-admin/users/stats/"
  );
  return response.data.data;
}

export async function createUser(data: CreateUserInput): Promise<UserDto> {
  const response = await apiClient.post<ApiSuccessEnvelope<UserDto>>(
    "/super-admin/users/",
    data
  );
  return response.data.data;
}

export async function updateUser(
  userId: string,
  data: UpdateUserInput
): Promise<UserDto> {
  const response = await apiClient.patch<ApiSuccessEnvelope<UserDto>>(
    `/super-admin/users/${userId}/`,
    data
  );
  return response.data.data;
}

export async function deleteUser(userId: string): Promise<void> {
  await apiClient.delete(`/super-admin/users/${userId}/`);
}

export async function disableUserTotp(
  userId: string,
  adminPassword: string
): Promise<UserDto> {
  const response = await apiClient.post<ApiSuccessEnvelope<UserDto>>(
    `/super-admin/users/${userId}/2fa/disable/`,
    { password: adminPassword }
  );
  return response.data.data;
}

// -------------------------------------------------------------
// TanStack Query Hooks
// -------------------------------------------------------------

export function useUsers(params: UsersQueryParams) {
  return useQuery({
    queryKey: ["users", params],
    queryFn: () => getUsers(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useUserStats() {
  return useQuery({
    queryKey: ["user-stats"],
    queryFn: getUserStats,
    refetchInterval: 60_000, // 60s background refresh
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserInput) => createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserInput }) =>
      updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    },
  });
}

export function useDisableUserTotp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      adminPassword,
    }: {
      userId: string;
      adminPassword: string;
    }) => disableUserTotp(userId, adminPassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
}

